import { definePlugin } from '../../framework';
import { EntryType } from '@morph-mapper/types';
import { resolvePrompt } from './prompt-registry';
import { useGlobalPluginStore } from '../../store';
import { suggestionTypeFor } from '../../utils';

// Cache variables
let cachedResult: string[] | null = null;
let cachedStructuredResult: any | null = null;
let cachedBaseTemplate: any | null = null;
let cachedPerFileScores: Record<number, Record<string, { score: number; rendered: string }>> = {};
let cachedFileTexts: string[] = [];
let processedFileTexts: Set<string> = new Set();

type ReturnState = {
  resultList: string[];
  domain?: Domain;
  fileType?: FileType;
};

export enum Domain {
  Intermodal = 'intermodal',
  Retail = 'retail',
  EasyEOM = 'easyeom',
}

export enum FileType {
  PDF = 'pdf',
  TABLE = 'xls/csv',
  EMAIL = 'email',
  TEXT = 'text',
  XML = 'xml',
  JSON = 'json',
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const normalizeAnchorExtractionText = (text: string): string =>
  text.replace(/\r\n/g, '\n');

export const parseAnchorExtractionModelText = (
  modelText: string,
): unknown | null => {
  try {
    const parsed = JSON.parse(modelText);

    if (!isRecord(parsed)) {
      return parsed;
    }

    for (const key of Object.keys(parsed)) {
      const field = parsed[key];
      if (Array.isArray(field)) {
        parsed[key] = { options: field };
      }
    } 

    for (const field of Object.values(parsed)) {
      if (!isRecord(field) || !Array.isArray(field['options'])) {
        continue;
      }

      for (const option of field['options'] as unknown[]) {
        if (isRecord(option) && typeof option['snippet'] === 'string') {
          (option as Record<string, unknown>)['snippet'] =
            (option['snippet'] as string)
              .trim()
              .replace(/:  ,/g, ':,  ,')
              .replace(/\\n/g, '\n')
              .replace(/<MS>/g, '  ')
              .replace(/\n{2,}/g, '\n');
        }
      }
    }

    return parsed;
  } catch {
    return null;
  }
};

export function getReferenceOptions(): Array<{ value: string; snippet: unknown; verified: boolean }> {
  if (!cachedStructuredResult) return [];

  const fieldData = (cachedStructuredResult as any)['clientreference'];
  if (!fieldData?.options) return [];

  const verifiedOptions = fieldData.options.filter((opt: any) => opt.verified !== false);
  const optionsToReturn =
    verifiedOptions.length > 0
      ? verifiedOptions
      : fieldData.options.filter((opt: any) => opt.snippet === opt.value);

  return optionsToReturn
    .filter((opt: any) => opt?.value && opt?.snippet)
    .map((opt: any) => ({
      value: String(opt.renderedValue ?? opt.value).trim(),
      snippet: opt.snippet,
      verified: opt.verified === true,
    }));
}

const verifyOption = async (
  opt: any,
  callRenderFn: (code: string) => Promise<any>
): Promise<{ passes: boolean; rendered: string; score: number }> => {
  // Skip verification for non-string snippets
  if (typeof opt.snippet !== 'string') {
    return { passes: false, rendered: '', score: 0 };
  }

  // Skip verification for enum/literal options (snippet === value)
  if (opt.snippet === opt.value) {
    return { passes: true, rendered: opt.value, score: 100 };
  }

  // Skip verification for column snippets
  if (/^#>\d+\$get$/.test(opt.snippet)) {
    return { passes: true, rendered: opt.value ?? '', score: 50 };
  }

  if (!opt.value || typeof opt.value !== 'string') {
    return { passes: false, rendered: '', score: 0 };
  }

  try {
    const renderResult = await callRenderFn(
      JSON.stringify(opt.snippet.replace(/<MS>/g, '  '))
    );

    if (!renderResult.isOk()) {
      return { passes: false, rendered: '(render failed)', score: -25 };
    }

    const rendered = String(renderResult.value).trim();
    const trimmedRendered = rendered.toLowerCase();
    const trimmedValue = opt.value.trim().toLowerCase();

    // Exact match
    if (trimmedRendered === trimmedValue) {
      return { passes: true, rendered, score: 100 };
    }

    // Bidirectional containment
    if (trimmedRendered.includes(trimmedValue)) {
      const lengthRatio = trimmedValue.length / trimmedRendered.length;
      const score = Math.floor(lengthRatio * 90);
      // Apply length penalty — rendered more than 2x expected is suspect
      const excessRatio = Math.max(0,
        (trimmedRendered.length - trimmedValue.length) /
        Math.max(trimmedValue.length, 1)
      );
      const penalty = Math.min(60, Math.floor(excessRatio * 30));
      const finalScore = Math.max(0, score - penalty);
      return { passes: finalScore >= 50, rendered, score: finalScore };
    }

    if (trimmedValue.includes(trimmedRendered) && trimmedRendered.length > 3) {
      return { passes: true, rendered, score: 75 };
    }

    // Character similarity
    const minLen = Math.min(trimmedRendered.length, trimmedValue.length);
    let matches = 0;
    for (let i = 0; i < minLen; i++) {
      if (trimmedRendered[i] === trimmedValue[i]) matches++;
    }
    const score = Math.floor((matches / trimmedValue.length) * 50);
    return { passes: false, rendered, score };

  } catch {
    return { passes: false, rendered: '(error)', score: -25 };
  }
};

export const anchorExtract = definePlugin<ReturnState>({
  name: 'Anchor Extract',
  version: '0.3.0',
  initialState: { resultList: [] },
  settings: {
    autofill: {
      name: 'Auto-fill suggestions',
      type: 'boolean',
      default: true,
    },
  },

  // STEP 1 — BOOTSTRAP
  bootstrap: async ({
    textContent,
    state,
    log,
    progress,
    extractWithLLM,
    callRender,
    createCallRender,
    getPaths,
    getValue,
  }) => {
    const { fileIndex, totalFiles } = useGlobalPluginStore.getState();
    const isFirstFile = fileIndex === 0;

    // A genuinely new session either has no prior scoring data at all, or its
    // file count doesn't match what we already scored against — stale data
    // from a previous multi-file run, not the one currently in progress.
    const scoredFileCount = Object.keys(cachedPerFileScores).length;
    const isNewSession = scoredFileCount === 0 || totalFiles !== scoredFileCount + 1;

    if (isFirstFile && isNewSession) {
      cachedBaseTemplate = null;
      cachedPerFileScores = {};
      cachedFileTexts = [];
      processedFileTexts = new Set();
    }

    if (processedFileTexts.has(textContent) && !isFirstFile) {
      cachedStructuredResult = JSON.parse(
        JSON.stringify(cachedBaseTemplate)
      );
      return;
    }

    progress(2, 'Starting');
    log(`[Bootstrap] Starting extraction workflow...`);
    log(
      `[Bootstrap] Domain: ${state.domain || 'unknown'} | FileType: ${
        state.fileType || 'unknown'
      }`,
    );

    progress(5, `Domain: ${state.domain || 'unknown'} · File type: ${state.fileType || 'unknown'}`);

    progress(8, 'Cleaning up text');
    const normalizedText = normalizeAnchorExtractionText(textContent);

    progress(10, 'Collecting simple fields');
    const simplePaths = getPaths(EntryType.Simple);

    progress(12, 'Collecting graph fields');
    const graphPaths = getPaths(EntryType.Graph);

    progress(14, 'Collecting cell fields');
    const cellPaths = getPaths(EntryType.Cell);

    const allPaths = [
      ...(simplePaths.isOk() ? simplePaths.value : []),
      ...(graphPaths.isOk() ? graphPaths.value : []),
      ...(cellPaths.isOk() ? cellPaths.value : []),
    ];

    progress(16, 'Removing duplicate fields');
    const seenKeys = new Set<string>();
    const dedupedPaths = allPaths.filter((path) => {
      const key = path[path.length - 1];
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });

    progress(18, `${dedupedPaths.length} field(s) to look for`);
    const fieldLines = dedupedPaths.map((path) => {
      const fieldKey = path[path.length - 1];
      const nodeResult = getValue(path);
      if (nodeResult.isOk() && nodeResult.value.semantics) {
        return `- ${fieldKey} (${nodeResult.value.semantics})`;
      }
      console.warn('[AnchorExtract] No semantics for field:', fieldKey);
      return `- ${fieldKey}`;
    });

    const enumLines: string[] = [];
    for (const path of dedupedPaths) {
      const fieldKey = path[path.length - 1];
      const nodeResult = getValue(path);
      if (!nodeResult.isOk()) continue;
      const node = nodeResult.value;
      if (
        node.validation?._def?.typeName === 'ZodEnum' &&
        Array.isArray(node.validation._def.values)
      ) {
        const values = node.validation._def.values as string[];
        enumLines.push(
          `- ${fieldKey}: [${values.map(v => `"${v}"`).join(', ')}]`
        );
      }
    }

    const enumsSection = enumLines.length > 0
      ? `\nENUM FIELDS — for these fields you MUST use only ` +
        `the exact values listed below, chosen to best match ` +
        `the document content:\n` +
        enumLines.join('\n') + '\n'
      : '';

    const fieldsSection =
      fieldLines.length > 0
        ? `REQUIRED FIELDS FOR THIS EXTRACTION:\n` +
          `Extract only the following fields.\n` +
          `Return "options": [] for any field not listed here:\n` +
          fieldLines.join('\n') +
          enumsSection +
          `\n\nDOCUMENT TEXT FOLLOWS:\n`
        : '';

    // console.log('[AnchorExtract] Fields section sent to LLM:\n', fieldsSection);

    const augmentedText = fieldsSection + normalizedText;

    // 2b — Call API (via Morphseek)
    progress(20, 'Sending request');
    log(`[Bootstrap] Sending request to Morphseek API`);

    // One AI call, so one message; the row shows the seconds it takes.
    const waitingMessage = `Extracting ${dedupedPaths.length} field(s)`;

    let simValue = 20;
    const ticker = setInterval(() => {
      simValue = Math.min(55, simValue + 0.3);
      progress(simValue, waitingMessage);
    }, 125);

    try {
      // Resolve prompt inside try — if domain/fileType combo is unregistered the bar won't freeze
      const entry = resolvePrompt(state.domain!, state.fileType!);
      console.log(`[2.1] Using ${entry.promptName} extraction prompt`);

      const response = await extractWithLLM(augmentedText, entry.promptName);
      clearInterval(ticker);

      if (!response.isOk()) {
        console.error(`[AnchorExtract] extractWithLLM error:`, response.error);
        throw new Error(`Morphseek error: ${JSON.stringify(response.error)}`);
      }

      const modelText = await response.value.text;

      if (!modelText) {
        throw new Error('No content returned from Morphseek API');
      }

      progress(60, 'Reading response');
      cachedResult = [modelText];

      // console.log(`[AnchorExtract] Raw modelText:`, modelText);

      const parsedModelText = parseAnchorExtractionModelText(modelText);

      if (parsedModelText === null) {
        console.error(`[AnchorExtract] Failed to parse modelText as JSON`);
        console.error(
          `[AnchorExtract] Raw text (first 500 chars):`,
          modelText.substring(0, 500),
        );
      } else {
        cachedStructuredResult = parsedModelText;

        for (const path of dedupedPaths) {
          const fieldKey = path[path.length - 1];
          const nodeResult = getValue(path);
          if (!nodeResult.isOk()) continue;
          const node = nodeResult.value;
          if (
            node.validation?._def?.typeName !== 'ZodEnum' ||
            !Array.isArray(node.validation._def.values)
          ) continue;

          const allEnumValues = node.validation._def.values as string[];
          const fieldData = (cachedStructuredResult as any)[fieldKey];
          if (!fieldData?.options) continue;

          const existingValues = new Set(
            fieldData.options.map((o: any) =>
              typeof o.value === 'string' ? o.value.toLowerCase() : o.value)
          );

          for (const enumValue of allEnumValues) {
            if (fieldData.options.length >= 4) break;
            if (!existingValues.has(enumValue.toLowerCase())) {
              fieldData.options.push({
                value: enumValue,
                snippet: enumValue,
              });
            }
          }
        }
      }

      // 2b.5 — Verify all options against real render
      if (cachedStructuredResult) {
        for (const [fieldKey, field] of Object.entries(cachedStructuredResult)) {
          const fieldData = field as any;
          if (!fieldData?.options || fieldData.options.length === 0) continue;

          for (const opt of fieldData.options) {
            if (typeof opt.snippet !== 'string') continue;
            const result = await verifyOption(opt, callRender);
            opt.verified = result.passes;
            opt.renderedValue = result.rendered;
            opt.verificationScore = result.score;
          }
        }
        console.log('[AnchorExtract] Verification complete');
      }

      // 2b.6 — Nearby position fallback for anchor snippets
      if (cachedStructuredResult) {
        for (const [fieldKey, field] of Object.entries(cachedStructuredResult)) {
          const fieldData = field as any;
          if (!fieldData?.options) continue;

          for (const opt of fieldData.options) {
            if (opt.verified === true) continue;
            if (typeof opt.snippet !== 'string') continue;
            // Only for anchor snippets, not column snippets
            if (/^#>\d+\$get$/.test(opt.snippet)) continue;
            if (!opt.snippet.startsWith('#')) continue;

            // Parse current jump count — "#2|..." has jump 2, "#|..." has jump 0
            const jumpMatch = opt.snippet.match(/^#(\d+)\|/);
            const currentJump = jumpMatch ? parseInt(jumpMatch[1]!) : 0;

            // Try ±1 and ±2
            for (const delta of [-1, 1, -2, 2]) {
              const newJump = currentJump + delta;
              if (newJump < 0) continue;

              // Build new snippet with adjusted jump
              const newSnippet = newJump === 0
                ? opt.snippet.replace(/^#\d+\|/, '#|')
                : opt.snippet.replace(/^#(\d+)?\|/, `#${newJump}|`);

              const tryOpt = { ...opt, snippet: newSnippet };
              const result = await verifyOption(tryOpt, callRender);

              if (result.passes) {
                opt.snippet = newSnippet;
                opt.verified = true;
                opt.renderedValue = result.rendered;
                opt.verificationScore = result.score;
                console.log(
                  `[AnchorExtract] Nearby fallback fixed ${fieldKey}:`,
                  `jump ${currentJump} → ${newJump}`
                );
                break;
              }
            }
          }
        }
      }

      if (cachedStructuredResult) {
        // 2d — Targeted retry loop with verification
        const MAX_RETRIES = 3;

        for (let retryRound = 0; retryRound < MAX_RETRIES; retryRound++) {
          // Collect still-failing fields (no verified option)
          const stillFailing: Array<{
            key: string; snippet: string; rendered: string
          }> = [];

          if (cachedStructuredResult) {
            for (const [fieldKey, field] of Object.entries(cachedStructuredResult)) {
              const fieldData = field as any;
              if (!fieldData?.options) continue;
              const hasVerified = fieldData.options.some((o: any) => o.verified === true);
              if (!hasVerified) {
                const topOpt = fieldData.options[0];
                stillFailing.push({
                  key: fieldKey,
                  snippet: topOpt?.snippet ?? '(none)',
                  rendered: topOpt?.renderedValue ?? '(not rendered)',
                });
              }
            }
          }

          if (stillFailing.length === 0) break;

          progress(
            64 + retryRound * 3,
            `Retrying ${stillFailing.length} field(s) (round ${retryRound + 1} of ${MAX_RETRIES})`
          );

          // Build context-windowed retry prompt
          const fieldLines = stillFailing.map(f => {
            // Extract ±500 char context window around the anchor text
            const anchorMatch = f.snippet.match(/#([^\n$,|]+)$/);
            const anchorText = anchorMatch ? anchorMatch[1]!.trim() : '';
            let contextWindow = '';
            if (anchorText && textContent.includes(anchorText)) {
              const idx = textContent.indexOf(anchorText);
              const start = Math.max(0, idx - 500);
              const end = Math.min(textContent.length, idx + 500);
              contextWindow = `\n    Context around anchor:\n    ...${
                textContent.slice(start, end).replace(/\n/g, '\n    ')
              }...`;
            }
            return `  - ${f.key}:\n` +
              `    tried snippet: "${f.snippet}"\n` +
              `    rendered: "${f.rendered}" (wrong or empty)\n` +
              `    expected value: "${
                (cachedStructuredResult as any)?.[f.key]?.options?.[0]?.value ?? ''
              }"${contextWindow}`;
          }).join('\n\n');

          const retryInstruction =
            `RETRY ROUND ${retryRound + 1} — These fields still need correct snippets.\n` +
            `Each entry shows what was tried and what it returned (wrong/empty).\n` +
            `Find better anchors. Do NOT reuse the same snippets shown below.\n` +
            `Extract ONLY these fields. Return "options": [] for any not listed.\n\n` +
            fieldLines +
            `\n\nDOCUMENT TEXT FOLLOWS:\n`;

          try {
            const retryResponse = await extractWithLLM(
              retryInstruction + augmentedText,
              entry.promptName
            );

            if (!retryResponse.isOk()) continue;

            const retryText = await retryResponse.value.text;
            let retryStructured: any;
            try {
              retryStructured = JSON.parse(retryText);
              // Normalize bare arrays
              for (const key of Object.keys(retryStructured)) {
                if (Array.isArray(retryStructured[key])) {
                  retryStructured[key] = { options: retryStructured[key] };
                }
              }
              // Post-process snippets
              for (const field of Object.values(retryStructured)) {
                const fd = field as any;
                if (fd?.options) {
                  for (const opt of fd.options) {
                    if (typeof opt.snippet === 'string') {
                      opt.snippet = opt.snippet
                        .trim()
                        .replace(/:  ,/g, ':,  ,')
                        .replace(/\\n/g, '\n')
                        .replace(/<MS>/g, '  ')
                        .replace(/\n{2,}/g, '\n');
                    }
                  }
                }
              }
            } catch {
              console.error('[AnchorExtract] Failed to parse retry response');
              continue;
            }

            // Verify retry results and merge only passing ones
            const failingKeys = stillFailing.map(f => f.key);
            for (const key of failingKeys) {
              const retryField = retryStructured[key];
              if (!retryField?.options || retryField.options.length === 0) continue;

              const existing = (cachedStructuredResult as any)[key];
              if (!existing?.options) continue;

              for (const retryOpt of retryField.options) {
                const result = await verifyOption(retryOpt, callRender);
                retryOpt.verified = result.passes;
                retryOpt.renderedValue = result.rendered;
                retryOpt.verificationScore = result.score;

                if (result.passes) {
                  // Prepend verified retry options so they sort first
                  existing.options.unshift(retryOpt);
                  console.log(
                    `[AnchorExtract] Retry round ${retryRound + 1} fixed: ${key}`
                  );
                }
              }
            }

          } catch (retryError) {
            console.error('[AnchorExtract] Retry call failed:', retryError);
          }
        }

        // 2e — Cross-field suggestion sharing for reference-type fields
        const referenceFields = Object.keys(cachedStructuredResult)
          .filter(key => key.toLowerCase().includes('reference'));

        if (referenceFields.length > 1) {
          for (const targetKey of referenceFields) {
            const targetField = (cachedStructuredResult as any)[targetKey];
            if (!targetField?.options) continue;

            const existingValues = new Set(
              targetField.options.map((o: any) =>
                typeof o.value === 'string' ? o.value.trim().toLowerCase() : o.value
              )
            );

            for (const sourceKey of referenceFields) {
              if (sourceKey === targetKey) continue;
              const sourceField = (cachedStructuredResult as any)[sourceKey];
              if (!sourceField?.options || sourceField.options.length === 0) continue;

              const topOption = sourceField.options[0];
              if (!topOption?.value) continue;

              const normalizedValue = typeof topOption.value === 'string'
                ? topOption.value.trim().toLowerCase()
                : topOption.value;

              if (!existingValues.has(normalizedValue)) {
                targetField.options.push({
                  ...topOption,
                  autofill: false,
                  crossField: true,
                });
                existingValues.add(normalizedValue);
              }
            }
          }
        }

          processedFileTexts.add(textContent);

          // 2f — Multi-file: capture file 1 as the base template, or score it against later files
          if (isFirstFile) {
            // Store file 1 results as the base template
            cachedBaseTemplate = JSON.parse(
              JSON.stringify(cachedStructuredResult)
            );
            cachedFileTexts.push(textContent);
          } else if (cachedBaseTemplate) {
            cachedFileTexts.push(textContent);

            // Score base template against this file's content
            const scores: Record<string, { score: number; rendered: string }> = {};

            for (const [fieldKey, field] of Object.entries(cachedBaseTemplate)) {
              const fieldData = field as any;
              if (!fieldData?.options || fieldData.options.length === 0) continue;

              const topOption = fieldData.options[0];
              if (typeof topOption.snippet !== 'string') continue;
              if (!topOption.value || typeof topOption.value !== 'string') continue;

              const snippetForRender = topOption.snippet.replace(/<MS>/g, '  ');
              const renderResult = await callRender(JSON.stringify(snippetForRender));

              if (!renderResult.isOk()) {
                scores[fieldKey] = { score: -25, rendered: '(render failed)' };
                continue;
              }

              const rendered = String(renderResult.value).trim().toLowerCase();
              const expected = topOption.value.trim().toLowerCase();

              if (rendered === expected) {
                scores[fieldKey] = { score: 100, rendered };
              } else {
                const minLen = Math.min(rendered.length, expected.length);
                let matches = 0;
                for (let i = 0; i < minLen; i++) {
                  if (rendered[i] === expected[i]) matches++;
                }
                scores[fieldKey] = {
                  score: Math.floor((matches / expected.length) * 50),
                  rendered,
                };
              }
            }

            cachedPerFileScores[fileIndex] = scores;
          }

          // 2g — Multi-file: targeted retry for poorly scored fields
          if (!isFirstFile && cachedBaseTemplate && cachedFileTexts.length > 0) {
            const RETRY_THRESHOLD = 50;

            const failingFields = Object.entries(cachedPerFileScores[fileIndex] ?? {})
              .filter(([, entry]) => entry.score < RETRY_THRESHOLD)
              .map(([fieldKey]) => fieldKey);

            if (failingFields.length > 0) {
              // Build stable vs unstable field summary
              const allFieldKeys = Object.keys(
                cachedPerFileScores[1] ?? {}
              );

              const stableFields = allFieldKeys.filter(fieldKey => {
                const fileScores = Object.entries(cachedPerFileScores)
                  .filter(([idx]) => Number(idx) < fileIndex)
                  .map(([, scores]) => scores[fieldKey]?.score ?? 0);
                return fileScores.length > 0 && fileScores.every(s => s >= 50);
              });

              const fieldLines = failingFields.map((fieldKey) => {
                const baseField = (cachedBaseTemplate as any)[fieldKey];
                const topOption = baseField?.options?.[0];
                if (!topOption) return `  - ${fieldKey}: no snippet available`;

                // Build per-file render history for this field
                const renderHistory = Object.entries(cachedPerFileScores)
                  .filter(([idx]) => Number(idx) < fileIndex)
                  .map(([idx, scores]) => {
                    const entry = scores[fieldKey];
                    if (!entry) return null;
                    return `    file ${Number(idx) + 1}: rendered "${entry.rendered}" ` +
                      `(score ${entry.score})`;
                  })
                  .filter(Boolean)
                  .join('\n');

                return `  - ${fieldKey}:\n` +
                  `    current snippet: "${topOption.snippet}"\n` +
                  `    expected value: "${topOption.value}"\n` +
                  `    history:\n${renderHistory || '    (no prior render data)'}`;
              }).join('\n\n');

              const stableSummary = stableFields.length > 0
                ? `\nFields stable across all files so far: ` +
                  `${stableFields.join(', ')}\n`
                : '';

              const retryInstruction =
                `MULTI-FILE RETRY — READ THIS FIRST:\n` +
                `This template is being generalized across ` +
                `${cachedFileTexts.length} files.\n` +
                stableSummary +
                `The following fields are not yet stable across ` +
                `all files. For each, the render history shows ` +
                `what the current snippet produced on previous ` +
                `files — use this to find a more stable anchor:\n\n` +
                fieldLines +
                `\n\nExtract ONLY these fields. Return "options": [] ` +
                `for any field not listed.\n\n` +
                `DOCUMENT TEXT FOLLOWS:\n`;

              try {
                const retryResponse = await extractWithLLM(
                  retryInstruction + textContent,
                  entry.promptName
                );

                if (retryResponse.isOk()) {
                  const retryText = await retryResponse.value.text;
                  try {
                    let retryStructured = JSON.parse(retryText);

                    // Normalize bare arrays
                    for (const key of Object.keys(retryStructured)) {
                      if (Array.isArray(retryStructured[key])) {
                        retryStructured[key] = { options: retryStructured[key] };
                      }
                    }

                    // Post-process snippets
                    for (const field of Object.values(retryStructured)) {
                      const fieldData = field as any;
                      if (fieldData?.options) {
                        for (const opt of fieldData.options) {
                          if (typeof opt.snippet === 'string') {
                            opt.snippet = opt.snippet
                              .trim()
                              .replace(/:  ,/g, ':,  ,')
                              .replace(/\\n/g, '\n')
                              .replace(/<MS>/g, '  ')
                              .replace(/\n{2,}/g, '\n');
                          }
                        }
                      }
                    }

                    for (const fieldKey of failingFields) {
                      const retryField = retryStructured[fieldKey];
                      if (!retryField?.options || retryField.options.length === 0) {
                        continue;
                      }

                      const retryTopOption = retryField.options[0];
                      if (typeof retryTopOption.snippet !== 'string') continue;

                      // Score retry snippet against file 2 (current file)
                      const retryFile2Result = await callRender(
                        JSON.stringify(retryTopOption.snippet.replace(/<MS>/g, '  '))
                      );
                      const retryFile2Score = retryFile2Result.isOk()
                        ? (String(retryFile2Result.value).trim().toLowerCase() ===
                          retryTopOption.value?.trim().toLowerCase() ? 100 : 25)
                        : -25;

                      // Score original base snippet against file 2
                      const baseFile2Score = cachedPerFileScores[fileIndex][fieldKey]?.score ?? 0;

                      // Score retry snippet against all previous files
                      let retryPreviousScore = 0;
                      let basePreviousScore = 0;
                      const retryScoresPerFile: number[] = [];

                      for (let i = 0; i < cachedFileTexts.length - 1; i++) {
                        const prevCallRender = createCallRender(cachedFileTexts[i]);

                        const retryPrevResult = await prevCallRender(
                          JSON.stringify(retryTopOption.snippet.replace(/<MS>/g, '  '))
                        );
                        const retryPrevScore = retryPrevResult.isOk()
                          ? (String(retryPrevResult.value).trim().toLowerCase() ===
                            retryTopOption.value?.trim().toLowerCase() ? 100 : 25)
                          : -25;
                        retryPreviousScore += retryPrevScore;
                        retryScoresPerFile.push(retryPrevScore);

                        const baseField = (cachedBaseTemplate as any)[fieldKey];
                        const baseTopOption = baseField?.options?.[0];

                        let basePrevScore = 100; // fallback only if no snippet exists
                        if (baseTopOption && typeof baseTopOption.snippet === 'string') {
                          const basePrevResult = await prevCallRender(
                            JSON.stringify(baseTopOption.snippet.replace(/<MS>/g, '  '))
                          );
                          basePrevScore = basePrevResult.isOk()
                            ? (String(basePrevResult.value).trim().toLowerCase() ===
                              baseTopOption.value?.trim().toLowerCase() ? 100 : 25)
                            : -25;
                        }
                        basePreviousScore += basePrevScore;
                      }

                      const retryCombinedScore = retryPreviousScore + retryFile2Score;
                      const baseCombinedScore = basePreviousScore + baseFile2Score;

                      // Keep whichever snippet has better combined score
                      if (retryCombinedScore > baseCombinedScore) {
                        (cachedBaseTemplate as any)[fieldKey] = retryField;
                        // Update stored scores with actual retry scores
                        for (let i = 0; i < retryScoresPerFile.length; i++) {
                          if (!cachedPerFileScores[i]) cachedPerFileScores[i] = {};
                          cachedPerFileScores[i][fieldKey] = {
                            score: retryScoresPerFile[i],
                            rendered: '(retry)',
                          };
                        }
                        cachedPerFileScores[fileIndex][fieldKey] = {
                          score: retryFile2Score,
                          rendered: String(retryFile2Result.isOk()
                            ? retryFile2Result.value
                            : '(render failed)').trim().toLowerCase(),
                        };
                      }
                    }
                  } catch (parseError) {
                    console.error('[AnchorExtract] Failed to parse multi-file retry:', parseError);
                  }
                }
              } catch (retryError) {
                console.error('[AnchorExtract] Multi-file retry call failed:', retryError);
              }
            }
          }

          if (!isFirstFile && cachedBaseTemplate) {
            cachedStructuredResult = JSON.parse(
              JSON.stringify(cachedBaseTemplate)
            );
          }
      }
    } catch (error) {
      clearInterval(ticker);
      console.error(`[AnchorExtract] Morphseek extraction failed:`, error);
      cachedResult = ['Error calling Morphseek API. See console.'];
    }
    progress(85, 'Extraction complete');
  },

  // STEP 2 — POST RENDER
  postRender: ({ setState, template }) => {
    if (cachedResult) {
      setState((draft) => {
        draft.resultList = cachedResult!;
      });
      console.log(`[3.1] Cached results applied.`);
      cachedResult = null;
    }
    return template;
  },

  // STEP 3 — FIELD SUGGESTIONS
  fieldSuggestions({ entry, settings }) {
    if (!cachedStructuredResult) {
      return [];
    }

    const fieldKey = entry.key;
    const fieldData = cachedStructuredResult[fieldKey];

    if (!fieldData || !fieldData.options) {
      return [];
    }

    const suggestionType = suggestionTypeFor(entry.type);

    const verifiedOptions = fieldData.options.filter(
      (opt: any) => opt.verified !== false
    );

    // If no verified options exist, fall back to all options
    // (handles fields that never went through verification,
    //  e.g. fields added by enum fallback or cross-field sharing)
    const optionsToReturn = verifiedOptions.length > 0
      ? verifiedOptions
      : fieldData.options.filter(
          (opt: any) => opt.snippet === opt.value  // enum/literal only
        );

    return optionsToReturn.map((opt: any) => ({
      value: opt.renderedValue ?? opt.value,
      snippet: opt.snippet ?? '',
      type: suggestionType,
      autofill: opt.autofill !== undefined
        ? opt.autofill
        : settings['autofill'],
      crossField: opt.crossField ?? false,
    }));
  },
});
